import React, { useEffect, useState } from 'react';
import { Search, MapPin, Star, Building2, X, Grid3x3, ArrowRight, Check } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

// Import your header and footer components
import Header from './Header';
import Footer from './Footer';
import MobileFooter from './MobileFooter';
import baseurl from '../../components/Baseurl/baseurl';
import { Button, Card, Heading, Eyebrow, Badge, Container, SectionHeader, EmptyState, Spinner, Avatar } from '../ui';

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" fill="#25D366" />
  </svg>
);

const PhoneIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor" />
  </svg>
);

const HomePage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTag, setSearchTag] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [categoriesList, setCategoriesList] = useState(['All']);
  const [categoriesData, setCategoriesData] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [appliedTag, setAppliedTag] = useState('');
  const [appliedLocation, setAppliedLocation] = useState('');
  const [appliedCategory, setAppliedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [userCity, setUserCity] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [ratings, setRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsError, setRatingsError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Helpers to resolve media URLs safely
  const toAbsoluteUrl = (path) => {
    if (!path) return '';
    const trimmed = String(path).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `${baseurl}/${trimmed.replace(/^\//, '')}`;
  };

  const getBannerImageUrl = (business) => {
    // Prefer explicit business profile image
    if (business?.business_profile_image) {
      return toAbsoluteUrl(business.business_profile_image);
    }

    // Fallback: first image (not video) from media_gallery
    const gallery = (business?.media_gallery || '').split(',').map((s) => s.trim()).filter(Boolean);
    const imageEntry = gallery.find((entry) => /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(entry));
    if (imageEntry) return toAbsoluteUrl(imageEntry);

    return '';
  };

  // Fetch ratings data
  const fetchRatings = async () => {
    try {
      setRatingsLoading(true);
      const response = await fetch(`${baseurl}/api/ratings/all`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch ratings');
      }
      const data = await response.json();
      const ratingsData = Array.isArray(data?.data) ? data.data : [];
      setRatings(ratingsData);
    } catch (err) {
      setRatingsError(err.message || 'Unable to load ratings');
      console.error('Error fetching ratings:', err);
    } finally {
      setRatingsLoading(false);
    }
  };

  // Get current user ID from localStorage
  useEffect(() => {
    const memberData = localStorage.getItem('memberData');
    if (memberData) {
      try {
        const parsedData = JSON.parse(memberData);
        setCurrentUserId(parsedData.mid);
      } catch (error) {
        console.error('Error parsing member data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseurl}/api/business-profile/all`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch businesses');
        }
        const data = await response.json();
        // Support both shapes: { profiles: [...] } and { success: true, data: [...] }
        const profiles = Array.isArray(data?.profiles)
          ? data.profiles
          : Array.isArray(data?.data)
            ? data.data
            : [];
        setBusinesses(profiles);
      } catch (err) {
        setError(err.message || 'Unable to load businesses');
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
    fetchRatings();
  }, []);

  // Fetch categories for dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${baseurl}/api/category/all`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch categories');
        }
        const data = await response.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        setCategoriesData(list);
        setCategoriesList(['All', ...list.map((c) => c.category_name)]);
      } catch (e) {
        console.error('Error fetching categories:', e);
      }
    };
    fetchCategories();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedTag, appliedLocation, appliedCategory, selectedTags, searchQuery]);

  // Add tag function
  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !selectedTags.includes(trimmedTag)) {
      setSelectedTags([...selectedTags, trimmedTag]);
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  // Remove tag function
  const removeTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  // Handle tag input key press
  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTagInput(value);
    setSearchQuery(value);
    // Auto-apply search as user types (with debounce)
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setAppliedTag(selectedTags.join(','));
      setAppliedLocation(searchLocation.trim());
      setAppliedCategory(activeCategory);
    }, 300);
  };

  // Get user's current location and city
  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }

    setLocationLoading(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Location obtained:', { latitude, longitude });
        setUserLocation({ lat: latitude, lng: longitude });

        // Get city name from coordinates using reverse geocoding
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          const data = await response.json();
          const city = data.city || data.locality || data.principalSubdivision || 'Unknown';
          console.log('User city detected:', city);
          setUserCity(city);
          setSearchLocation(`Near Me (${city})`);
          setAppliedLocation('Near Me');
        } catch (geocodeError) {
          console.error('Reverse geocoding failed:', geocodeError);
          setSearchLocation('Near Me');
          setAppliedLocation('Near Me');
        }

        setLocationLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to retrieve your location. ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access and try again.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'Please try again.';
            break;
        }
        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000
      }
    );
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  };

  // Calculate ratings for a specific business
  const getBusinessRatings = (businessId) => {
    const businessRatings = ratings.filter((rating) => {
      const rBid = parseInt(rating.business_id, 10);
      const bId = parseInt(businessId, 10);
      return !Number.isNaN(rBid) && !Number.isNaN(bId) && rBid === bId;
    });

    if (businessRatings.length === 0) {
      return { averageRating: 0, reviewCount: 0 };
    }

    const totalRating = businessRatings.reduce((sum, rating) => sum + Number(rating.rating || 0), 0);
    const averageRating = totalRating / businessRatings.length;

    return {
      averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
      reviewCount: businessRatings.length
    };
  };

  // derive unique tags and locations for suggestions
  const uniqueTags = Array.from(
    new Set(
      businesses
        .flatMap((b) =>
          (typeof b?.tags === 'string' ? b.tags : (Array.isArray(b?.tags) ? b.tags.join(',') : ''))
            .split(',')
            .map((t) => String(t).trim())
            .filter((t) => t)
        )
    )
  );

  const uniqueLocations = Array.from(
    new Set(
      businesses
        .map((b) => String(b?.city ?? b?.location ?? '').trim())
        .filter((v) => v)
    )
  );

  const tagSuggestions = tagInput
    ? uniqueTags
      .filter((t) => String(t).toLowerCase().startsWith(String(tagInput).toLowerCase()))
      .slice(0, 8)
    : [];

  const locationSuggestions = searchLocation
    ? uniqueLocations
      .filter((l) => String(l).toLowerCase().startsWith(String(searchLocation).toLowerCase()))
      .slice(0, 8)
    : [];

  // Function to assign colors to categories
  const getCategoryColor = (categoryName) => {
    const colors = [ "bg-primary/10 text-primary", "bg-info/10 text-info", "bg-accent/10 text-accent", "bg-warning/10 text-warning", "bg-success/10 text-success", "bg-primary/10 text-primary-hi", "bg-accent/10 text-accent", "bg-danger/10 text-danger"
    ];

    // Use category name to consistently assign colors
    const hash = categoryName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    return colors[Math.abs(hash) % colors.length];
  };

  // Calculate real categories from business data EXCLUDING current user's businesses
  const realCategories = React.useMemo(() => {
    // Filter out current user's businesses before counting categories
    const filteredBusinesses = currentUserId
      ? businesses.filter(business => business?.member_id !== currentUserId)
      : businesses;

    const categoryCounts = {};
    filteredBusinesses.forEach(business => {
      const category = business?.business_type || 'Other';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    return Object.entries(categoryCounts)
      .map(([name, count]) => ({
        name,
        count: count.toString(),
        color: getCategoryColor(name)
      }))
      .sort((a, b) => parseInt(b.count) - parseInt(a.count)); // Sort by count descending
  }, [businesses, currentUserId]);

  return (
    <div className="min-h-screen bg-bg pb-20 lg:pb-0">
      {/* Uncomment when you have the Header component */}
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-navy on-dark">
        <Container className="relative py-10 sm:py-12">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded border border-white/12 bg-surface/5 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
              <Building2 className="h-3 w-3" />
              Kongu Business Community
            </span>
            <Heading variant="display" className="mt-3">
              Discover local <em className="kbc-serif">businesses</em>
            </Heading>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-text">
              Connect with trusted businesses in your community.
            </p>
          </div>

          {/* Search Section */}
          <Card className="mx-auto mt-6 max-w-5xl p-3 sm:p-4">
            <div className="flex flex-col gap-2 lg:flex-row">
              <div className="flex-1">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                  <div className="flex min-h-[34px] w-full flex-wrap items-center gap-1.5 rounded-md border border-line/15 bg-surface2 py-1 pl-9 pr-3 text-ink transition-colors duration-150 focus-within:border-primary/50">
                    {/* Selected Tags */}
                    {selectedTags.map((tag, index) => (
                      <Badge key={index} tone="primary" className="gap-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-0.5 text-primary transition-colors hover:text-primary-hi"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                    {/* Tag Input */}
                    <input
                      type="text"
                      placeholder={selectedTags.length === 0 ? "Search Businesses, Tags..." : "Add more tags..."}
                      value={tagInput}
                      onChange={handleSearchChange}
                      onKeyPress={handleTagInputKeyPress}
                      onFocus={() => setShowTagSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
                      className="min-w-[150px] flex-1 bg-transparent text-[0.95rem] text-ink outline-none placeholder:text-muted/70 sm:min-w-[200px]"
                    />
                  </div>
                  {showTagSuggestions && tagSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 z-10 mt-2 max-h-48 overflow-auto rounded-md border border-line/12 bg-surface shadow-lift">
                      {tagSuggestions.map((s) => (
                        <div
                          key={s}
                          className="cursor-pointer px-3 py-2 text-sm text-text transition-colors hover:bg-surface2"
                          onMouseDown={() => {
                            addTag(s);
                          }}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 lg:max-w-xs">
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                  <input
                    type="text"
                    placeholder="Enter Location"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    onFocus={() => setShowLocationSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 150)}
                    className="h-[34px] w-full rounded-md border border-line/15 bg-surface2 pl-9 pr-16 text-sm text-ink outline-none transition-colors duration-150 placeholder:text-muted/70 focus:border-primary/50"
                  />
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={locationLoading}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-xs font-semibold text-primary transition-colors duration-150 hover:bg-primary/10 disabled:opacity-50"
                  >
                    {locationLoading ? '...' : 'Near Me'}
                  </button>
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 z-10 mt-2 max-h-48 overflow-auto rounded-md border border-line/12 bg-surface shadow-lift">
                      {locationSuggestions.map((s) => (
                        <div
                          key={s}
                          className="cursor-pointer px-3 py-2 text-sm text-text transition-colors hover:bg-surface2"
                          onMouseDown={() => {
                            setSearchLocation(s);
                            setShowLocationSuggestions(false);
                          }}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {locationError && (
                  <p className="mt-1 text-xs text-danger">{locationError}</p>
                )}
              </div>

              <div className="lg:w-48">
                <div className="relative">
                  <select
                    className="h-[34px] w-full appearance-none rounded-md border border-line/15 bg-surface2 pl-3 pr-9 text-sm text-ink outline-none transition-colors duration-150 focus:border-primary/50"
                    value={activeCategory}
                    onChange={(e) => {
                      const val = e.target.value;
                      setActiveCategory(val);
                    }}
                  >
                    {categoriesList.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <svg
                    className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted"
                    width="14" height="14" viewBox="0 0 12 8" fill="none" aria-hidden="true"
                  >
                    <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </div>
              </div>

              <Button
                size="lg"
                leftIcon={<Search className="h-4 w-4" />}
                onClick={() => {
                  console.log('Search clicked:', { selectedTags, searchQuery, searchLocation, activeCategory });
                  setAppliedTag(selectedTags.join(','));
                  setAppliedLocation(searchLocation.trim());
                  setAppliedCategory(activeCategory);
                }}
              >
                Search Now
              </Button>
            </div>

            {/* Helper: apply on Enter key anywhere in the search row */}
            <input
              type="text"
              className="hidden"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedTag(selectedTags.join(','));
                  setAppliedLocation(searchLocation.trim());
                  setAppliedCategory(activeCategory);
                }
              }}
            />

          </Card>
        </Container>
      </section>

      {/* Featured Businesses Section */}
      <Container className="py-8 sm:py-10">
        {/* Clean Section Header */}
        <div className="mb-10 text-center sm:mb-12">
          <Eyebrow className="justify-center">Curated directory</Eyebrow>
          <Heading variant="section" className="mx-auto mt-4">
            Featured <em className="kbc-serif">businesses</em>
          </Heading>
          <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-text sm:text-lg">
            Discover trusted local businesses handpicked for quality and excellence.
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="mb-2.5 flex items-center gap-2">
            <span className="h-px w-3.5 bg-brand" aria-hidden="true"></span>
            <h3 className="font-display text-sm font-semibold tracking-tight text-ink">Filter by Category</h3>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {/* Show "All" first, then top 6 categories, then "More" button */}
            {(() => {
              const topCategories = ['All', ...categoriesList.slice(1, 7)]; // Show "All" + top 6 categories
              const hasMore = categoriesList.length > 7;

              return (
                <>
                  {topCategories.map((category) => {
                    const isActive = activeCategory === category;
                    return (
                      <button
                        key={category}
                        onClick={() => {
                          setActiveCategory(category);
                          setAppliedCategory(category);
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition-colors duration-150 ${isActive
                          ? 'bg-primary text-onprimary hover:bg-primary-hi'
                          : 'bg-surface text-text ring-1 ring-inset ring-line/15 hover:text-ink hover:ring-line/30'
                          }`}
                      >
                        {category}
                        {isActive && <Check className="h-3 w-3" />}
                      </button>
                    );
                  })}

                  {/* More Categories Button */}
                  {hasMore && (
                    <button
                      onClick={() => setShowCategoryModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-surface px-2.5 py-1 text-sm font-medium text-text ring-1 ring-inset ring-line/15 transition-colors duration-150 hover:text-ink hover:ring-line/30"
                    >
                      <Grid3x3 className="h-3 w-3" />
                      More Categories
                      <Badge tone="primary" className="ml-0.5">
                        +{categoriesList.length - 7}
                      </Badge>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Category Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4 animate-fadeIn">
            <div className="w-full max-w-4xl max-h-[80vh] overflow-hidden rounded-lg border border-line/12 bg-surface shadow-lift animate-slideUp">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-line/12 p-6">
                <div>
                  <Heading variant="title" as="h3">All <em className="kbc-serif">categories</em></Heading>
                  <p className="mt-1 text-sm text-muted">Choose a category to filter businesses</p>
                </div>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full text-text transition-colors hover:bg-surface2 hover:text-ink"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto p-6 max-h-[calc(80vh-120px)]">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {categoriesList.map((category) => {
                    const isActive = activeCategory === category;
                    return (
                      <button
                        key={category}
                        onClick={() => {
                          setActiveCategory(category);
                          setAppliedCategory(category);
                          setShowCategoryModal(false);
                        }}
                        className={`rounded-md p-4 text-left text-sm font-medium transition-all duration-200 ${isActive
                          ? 'bg-primary text-onprimary shadow-primary'
                          : 'bg-surface2 text-ink ring-1 ring-inset ring-line/12 hover:bg-line/10'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate">{category}</span>
                          {isActive && <Check className="ml-2 h-4 w-4 shrink-0" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {(appliedTag || appliedLocation || appliedCategory !== 'All') && (
          <div className="mb-6 rounded-md border border-primary/20 bg-primary/5 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted sm:text-sm">Active filters:</span>
              {appliedTag && (
                <Badge tone="primary">Tags: {appliedTag}</Badge>
              )}
              {appliedLocation && (
                <Badge tone="primary">Location: {appliedLocation}</Badge>
              )}
              {appliedCategory !== 'All' && (
                <Badge tone="primary">Category: {appliedCategory}</Badge>
              )}
              <button
                onClick={() => {
                  setAppliedTag('');
                  setAppliedLocation('');
                  setAppliedCategory('All');
                  setSearchTag('');
                  setSearchLocation('');
                  setActiveCategory('All');
                  setSelectedTags([]);
                  setTagInput('');
                  setSearchQuery('');
                }}
                className="text-xs font-medium text-danger underline transition-colors hover:opacity-80"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Business Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="py-8 text-center text-danger">{error}</div>
        ) : (() => {
          // Filter businesses first - EXCLUDE CURRENT USER'S BUSINESSES
          const filteredBusinesses = businesses.filter((b) => {
            // Hide user's own business profile
            if (currentUserId && b?.member_id === currentUserId) {
              return false;
            }

            let matchCategory = true;
            if ((appliedCategory || 'All') !== 'All') {
              const selectedCategory = categoriesData.find((c) => c.category_name === appliedCategory);
              matchCategory = selectedCategory ? Number(b?.category_id) === Number(selectedCategory.cid) : false;
            }
            const tagText = String(b?.tags ?? '').toLowerCase();
            const nameText = String(b?.company_name ?? '').toLowerCase();
            const typeText = String(b?.business_type ?? '').toLowerCase();
            const appliedTags = appliedTag ? String(appliedTag).split(',').map(t => String(t).trim().toLowerCase()) : [];
            const searchQueryLower = String(searchQuery ?? '').toLowerCase().trim();

            // Match selected tags
            const matchSelectedTags = appliedTags.length === 0 || appliedTags.some(tag =>
              tagText.includes(tag) || nameText.includes(tag) || typeText.includes(tag)
            );

            // Match search query (normal search functionality)
            const matchSearchQuery = searchQueryLower === '' ||
              tagText.includes(searchQueryLower) ||
              nameText.includes(searchQueryLower) ||
              typeText.includes(searchQueryLower);

            const matchTag = matchSelectedTags && matchSearchQuery;

            // Handle "Near Me" location filtering
            let matchLocation = true;
            if (appliedLocation && String(appliedLocation).toLowerCase().trim() === 'near me') {
              if (userCity) {
                // Filter businesses by user's city
                const businessCity = String(b?.city ?? '').toLowerCase();
                const businessLocation = String(b?.location ?? '').toLowerCase();
                const userCityLower = userCity.toLowerCase();

                matchLocation = businessCity.includes(userCityLower) ||
                  businessLocation.includes(userCityLower) ||
                  businessCity.includes(userCityLower.split(' ')[0]); // Match first word of city

                console.log(`Near Me filter: User city "${userCityLower}" matches business city "${businessCity}" or location "${businessLocation}" = ${matchLocation}`);
              } else {
                console.log('Near Me selected but no user city available');
                matchLocation = false; // Don't show any if no city detected
              }
            } else if (appliedLocation && String(appliedLocation).trim() !== '') {
              const locText = String(b?.city ?? b?.location ?? '').toLowerCase();
              const searchLocLower = String(appliedLocation).toLowerCase().trim();
              matchLocation = locText.includes(searchLocLower);
              console.log(`Location filter: "${searchLocLower}" in "${locText}" = ${matchLocation}`);
            } else {
              matchLocation = true; // No location filter applied
            }

            return matchCategory && matchTag && matchLocation;
          });

          // Calculate pagination
          const totalItems = filteredBusinesses.length;
          const totalPages = Math.ceil(totalItems / itemsPerPage);
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          const currentItems = filteredBusinesses.slice(startIndex, endIndex);

          if (totalItems === 0) {
            return (
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title="No businesses found"
                description="Try adjusting your search, filters or location to see more results."
              />
            );
          }

          return (
            <>
              <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {currentItems.map((business) => {
                  const imageUrl = getBannerImageUrl(business);

                  const title = business?.company_name || 'Business';
                  const subtitle = business?.business_type || (business?.Member?.first_name || business?.member?.first_name || '');
                  const memberFirstName = business?.member?.first_name || business?.Member?.first_name || '';
                  const memberLastName = business?.member?.last_name || business?.Member?.last_name || '';
                  const memberName = [memberFirstName, memberLastName].filter(Boolean).join(' ');

                  // Get real ratings data
                  const { averageRating, reviewCount } = getBusinessRatings(business.id);
                  const reviewsCount = reviewCount;
                  const ratingValue = averageRating;
                  return (
                    <Card key={business.id} interactive padding="none" className="overflow-hidden">
                      <div className="relative">
                        <Link to={`/details/${business.id}`} className="block">
                          {/* Banner header section */}
                          {/* Neutral surface, not the brand green: this block is
                              1/3 of the card and a saturated accent at that size
                              overwhelms the grid. /fallback.png does not exist,
                              so an icon stands in when there is no image. */}
                          <div
                            className="flex h-24 w-full items-center justify-center border-b border-line/10 bg-surface2 bg-cover bg-center bg-no-repeat"
                            style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
                          >
                            {!imageUrl && <Building2 className="h-6 w-6 text-muted" aria-hidden="true" />}
                          </div>

                          {/* Avatar and member name positioned on the right side between banner and content */}
                          <div className="absolute right-5 top-14 z-10 flex flex-col items-end">
                            <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-surface bg-surface shadow-card">
                              <Avatar
                                src={imageUrl ? `${baseurl}/${business.Member?.profile_image}` : undefined}
                                name={memberName}
                                size="lg"
                                className="h-12 w-12"
                              />
                            </span>
                            {memberName && (
                              <div className="mt-1 rounded-full bg-surface/85 px-2 py-0.5 text-right text-sm font-medium text-ink">
                                {memberName}
                              </div>
                            )}
                          </div>

                          {/* Content section with left alignment - all inside Link */}
                          <div className="px-4 pb-3 pt-12 text-left">
                            <h3 className="mb-2 font-display text-lg font-semibold tracking-tight text-ink sm:text-xl">{title}</h3>
                            <p className="mb-3 text-sm text-text sm:text-base">{subtitle}</p>
                            <div className="mb-3 flex items-center gap-2 sm:gap-3">
                              <div className="flex items-center text-warning">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`h-3 w-3 sm:h-4 sm:w-4 ${star <= Math.floor(ratingValue)
                                      ? 'fill-warning text-warning'
                                      : star === Math.ceil(ratingValue) && ratingValue % 1 !== 0
                                        ? 'fill-warning/40 text-warning'
                                        : 'text-line/40'
                                      }`}
                                  />
                                ))}
                              </div>
                              <div className="flex items-baseline gap-1 sm:gap-2">
                                <span className="text-sm font-semibold text-ink sm:text-lg">
                                  {ratingValue > 0 ? ratingValue.toFixed(1) : ''}
                                </span>
                                <span className="text-xs text-muted sm:text-sm">
                                  ({reviewsCount} {reviewsCount === 1 ? 'review' : 'reviews'})
                                </span>
                              </div>
                            </div>
                            <div className="mb-4 text-left text-xs text-text sm:text-sm">Best time to contact : <span className="font-medium text-ink">{business?.best_contact_time || 'Morning'}</span></div>
                          </div>
                        </Link>

                        {/* Action buttons outside Link to avoid nested <a> */}
                        <div className="flex items-center gap-2 px-4 pb-4">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/details/${business.id}`)}
                          >
                            View
                          </Button>
                          <div className="ml-auto flex items-center gap-2">
                            <a
                              href={`tel:${business.business_work_contract || business.phone || ''}`}
                              className="text-info transition-colors hover:opacity-80"
                              title="Call"
                            >
                              <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </a>
                            <button
                              type="button"
                              className="cursor-pointer border-0 bg-transparent p-0 transition-colors hover:opacity-80"
                              title="WhatsApp"
                            >
                              <WhatsAppIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mb-8 flex flex-col items-center justify-between gap-4 sm:flex-row">
                  {/* Results info */}
                  <div className="text-xs text-muted sm:text-sm">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} businesses
                  </div>

                  {/* Pagination buttons */}
                  <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="hidden rounded-md border border-line/15 px-3 py-2 text-xs text-ink transition-colors hover:bg-surface2 disabled:cursor-not-allowed disabled:opacity-50 sm:inline-block sm:text-sm"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="rounded-md border border-line/15 px-3 py-2 text-xs text-ink transition-colors hover:bg-surface2 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`rounded-md border px-3 py-2 text-xs transition-colors sm:text-sm ${currentPage === pageNum
                              ? 'border-primary bg-primary text-onprimary'
                              : 'border-line/15 text-ink hover:bg-surface2'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="rounded-md border border-line/15 px-3 py-2 text-xs text-ink transition-colors hover:bg-surface2 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="hidden rounded-md border border-line/15 px-3 py-2 text-xs text-ink transition-colors hover:bg-surface2 disabled:cursor-not-allowed disabled:opacity-50 sm:inline-block sm:text-sm"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}

              {/* Load More Button (alternative to pagination) */}
              {currentPage < totalPages && (
                <div className="mb-8 text-center">
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Load More Businesses ({totalItems - endIndex} remaining)
                  </Button>
                </div>
              )}
            </>
          );
        })()}
      </Container>

      {/* Browse by Category */}
      <div className="bg-surface2 py-8 sm:py-10">
        <Container>
          <SectionHeader
            className="mb-8 sm:mb-10"
            title={<>Business <em className="kbc-serif">types</em></>}
            actions={
              <Button variant="ghost" onClick={() => navigate('/categories')} rightIcon={<ArrowRight className="h-4 w-4" />}>
                View All Categories
              </Button>
            }
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
            {realCategories.slice(0, 4).map((category) => (
              <Card
                key={category.name}
                interactive
                className="cursor-pointer"
                onClick={() => {
                  setActiveCategory(category.name);
                  setAppliedCategory(category.name);
                }}
              >
                <div className="flex flex-col items-center justify-center text-center">
                  <Badge tone="primary" className="mb-3">
                    {category.name}
                  </Badge>
                  <div className="font-display text-2xl font-semibold tracking-tighter text-ink sm:text-3xl">
                    {category.count}
                  </div>
                  <span className="mt-1 text-xs text-muted">businesses</span>
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </div>

      {/* Call to Action */}
      <section className="relative overflow-hidden bg-navy on-dark">
        <Container className="relative py-8 text-center sm:py-10">
          <Heading variant="section">
            Ready to list your <em className="kbc-serif">business</em>?
          </Heading>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-text">
            Join thousands of local businesses and reach more customers.
          </p>
          <div className="mt-8 flex justify-center">
            <Button variant="accent" size="lg" rightIcon={<ArrowRight className="h-4 w-4" />}>
              List Your Business
            </Button>
          </div>
        </Container>
      </section>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default HomePage;
